<template></template>
<script setup lang="ts">
import type { Head } from 'unhead/types'
import { contentTypes } from '~/models'
import { sanitize } from '~/utils/string-helper'

// An entry in the `script`/`noscript` array accepted by useHead. Unlike the raw
// `Script` schema type, this includes unhead's tag options (tagPriority,
// tagPosition, innerHTML) and accepts a partially-built ({}) object.
type ScriptEntry = NonNullable<Head['script']>[number]

const siteStore = useSiteStore()
let scripts = Array<ScriptEntry>()
let noscripts = Array<ScriptEntry>()

for (const script of siteStore.scriptTags) {
  const scriptModel: ScriptEntry = {}
  let isNoscript = false

  if (script.elements.innerCode.value.length > 0) {
    // Check if content is wrapped in noscript tags
    isNoscript = isWrappedInNoscript(script.elements.innerCode.value)
    
    // Remove script or noscript tags and get clean content
    const cleanedContent = isNoscript 
      ? removeNoscriptTag(script.elements.innerCode.value)
      : removeScriptTag(script.elements.innerCode.value)
    
    // eslint-disable-next-line no-unsanitized/property
    scriptModel.innerHTML = cleanedContent
  }

  const attributes = getAttributesFromText(script.elements.innerCode.value)

  attributes.forEach((at) => {
    //@ts-ignore
    scriptModel[at.key] = sanitize(at.value)
  })

  if (script.elements.url.value.length > 0)
    scriptModel.src = sanitize(script.elements.url.value)

  scriptModel.tagPriority = 'high'
  scriptModel.tagPosition =
    script.elements.location.value.length > 0
      ? getPosition(sanitize(script.elements.location.value[0].codename))
      : 'head'

  // Add to appropriate array based on tag type
  if (isNoscript) {
    noscripts.push(scriptModel)
  } else {
    scripts.push(scriptModel)
  }
}

if (scripts.length > 0 || noscripts.length > 0) {
  const headConfig: any = {}
  
  if (scripts.length > 0) {
    headConfig.script = scripts
  }
  
  if (noscripts.length > 0) {
    headConfig.noscript = noscripts
  }
  
  useHead(headConfig)
}

function getAttributesFromText(script: string) {
  const attributes: { key: string; value: string }[] = []
  
  // Try to match script tags first, then noscript tags
  let tagMatch = script.match(/<script\b([^>]*)>/i) || 
                 script.match(/<noscript\b([^>]*)>/i) ||
                 script.match(/&lt;script\b([^&gt;]*)&gt;/i) ||
                 script.match(/&lt;noscript\b([^&gt;]*)&gt;/i)
  
  if (tagMatch && tagMatch[1]) {
    const attrString = tagMatch[1]
    const regex = /([\w-]+)(?:\s*=\s*(["'])(.*?)\2)?/g
    let match
    while ((match = regex.exec(attrString)) !== null) {
      attributes.push({ key: match[1], value: match[3] || '' })
    }
  }
  return attributes
}

function getPosition(value: string) {
  switch (value) {
    case contentTypes.script_tag.elements.location.options.head.codename:
      return 'head'
    case contentTypes.script_tag.elements.location.options.body_close.codename:
      return 'bodyClose'
    case contentTypes.script_tag.elements.location.options.body_open.codename:
      return 'bodyOpen'
    default:
      return 'head'
  }
}

function removeScriptTag(html: string) {
  // Handle both HTML-encoded and raw script tags
  let result = html
  
  // Handle HTML-encoded script tags
  result = result.replace(/&lt;script\b[^&gt;]*&gt;([\s\S]*?)&lt;\/script&gt;/gi, '$1')
  
  // Handle raw script tags
  result = result.replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gi, '$1')
  
  return result
}

function removeNoscriptTag(html: string) {
  // Handle both HTML-encoded and raw noscript tags
  let result = html
  
  // Handle HTML-encoded noscript tags
  result = result.replace(/&lt;noscript\b[^&gt;]*&gt;([\s\S]*?)&lt;\/noscript&gt;/gi, '$1')
  
  // Handle raw noscript tags
  result = result.replace(/<noscript\b[^>]*>([\s\S]*?)<\/noscript>/gi, '$1')
  
  return result
}

function isWrappedInNoscript(html: string): boolean {
  // Check for both HTML-encoded and raw noscript tags
  return /&lt;noscript\b[^&gt;]*&gt;/i.test(html) || /<noscript\b[^>]*>/i.test(html)
}
</script>
